import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

function STLThumbnail({ filePath, width = 200, height = 150 }) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const [renderFailed, setRenderFailed] = useState(false);

  useEffect(() => {
    if (!mountRef.current || !filePath) return;

    try {
      // Construct full API URL
      let apiUrl = filePath;
      if (filePath.startsWith('/jobs/') || filePath.startsWith('/laser-jobs/')) {
        const baseURL = process.env.NODE_ENV === 'development' 
          ? (process.env.REACT_APP_API_BASE || '/api')
          : '/hub/api';
        apiUrl = baseURL + filePath;
      }

      // Check if it's a zip file (can't render as STL)
      if (filePath.toLowerCase().endsWith('.zip')) {
        setRenderFailed(true);
        return;
      }

      // Scene setup
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf5f5f5);
      sceneRef.current = scene;

      const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
      camera.position.z = 100;

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      
      // Check if WebGL context was actually created
      if (!renderer.getContext()) {
        throw new Error('WebGL context not available');
      }
      
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      mountRef.current.appendChild(renderer.domElement);

      // Lighting
      const light1 = new THREE.DirectionalLight(0xffffff, 0.8);
      light1.position.set(100, 100, 100);
      scene.add(light1);

      const light2 = new THREE.DirectionalLight(0xffffff, 0.4);
      light2.position.set(-100, -100, 100);
      scene.add(light2);

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(ambientLight);

      // Load STL file
      fetch(apiUrl)
        .then(response => response.arrayBuffer())
        .then(buffer => {
          const geometry = parseSTL(buffer);
          geometry.computeBoundingBox();
          geometry.center();

          const material = new THREE.MeshPhongMaterial({
            color: 0x00a8ff,
            shininess: 100,
            side: THREE.DoubleSide
          });

          const mesh = new THREE.Mesh(geometry, material);
          scene.add(mesh);

          // Auto-scale to fit view
          const boundingBox = geometry.boundingBox;
          const size = boundingBox.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const fov = camera.fov * (Math.PI / 180);
          let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
          cameraZ *= 1.5;
          
          // Position camera for side view from slightly above (rotated 90 degrees)
          camera.position.set(-.9*cameraZ, cameraZ *.7 , -.5*cameraZ);
          camera.lookAt(0, 0, 0);

          // Render
          renderer.render(scene, camera);
        })
        .catch(err => {
          console.error('Error loading STL:', err);
          showFallback();
        });

      return () => {
        if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
          mountRef.current.removeChild(renderer.domElement);
        }
        renderer.dispose();
      };
    } catch (err) {
      console.error('WebGL Error:', err);
      showFallback();
    }
    
    function showFallback() {
      // Don't display anything if rendering fails
      setRenderFailed(true);
    }
  }, [filePath, width, height]);

  if (renderFailed) {
    return null;
  }

  return <div ref={mountRef} style={{ width, height, overflow: 'hidden', borderRadius: '4px' }} />;
}

function parseSTL(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  const isASCII = isASCIISTL(arrayBuffer);

  if (isASCII) {
    return parseASCIISTL(arrayBuffer);
  } else {
    return parseBinarySTL(arrayBuffer);
  }
}

function isASCIISTL(arrayBuffer) {
  const view = new Uint8Array(arrayBuffer);
  const header = String.fromCharCode.apply(null, view.slice(0, 5));
  return header === 'solid';
}

function parseBinarySTL(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  const triangles = view.getUint32(80, true);

  const geometry = new THREE.BufferGeometry();
  const vertices = [];
  const normals = [];

  let offset = 84;
  for (let i = 0; i < triangles; i++) {
    const nx = view.getFloat32(offset, true);
    offset += 4;
    const ny = view.getFloat32(offset, true);
    offset += 4;
    const nz = view.getFloat32(offset, true);
    offset += 4;

    for (let j = 0; j < 3; j++) {
      vertices.push(view.getFloat32(offset, true));
      offset += 4;
      vertices.push(view.getFloat32(offset, true));
      offset += 4;
      vertices.push(view.getFloat32(offset, true));
      offset += 4;

      normals.push(nx, ny, nz);
    }

    offset += 2; // attribute byte count
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(normals), 3));

  return geometry;
}

function parseASCIISTL(arrayBuffer) {
  const text = String.fromCharCode.apply(null, new Uint8Array(arrayBuffer));
  const geometry = new THREE.BufferGeometry();
  const vertices = [];
  const normals = [];

  const pattern = /facet\s+normal\s+([-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?)\s+([-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?)\s+([-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?)\s+outer\s+loop\s+(vertex\s+([-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?)\s+([-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?)\s+([-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?)\s+){3}endloop\s+endfacet/g;

  let match;
  while ((match = pattern.exec(text)) !== null) {
    const nx = parseFloat(match[1]);
    const ny = parseFloat(match[3]);
    const nz = parseFloat(match[5]);

    const vertexPattern = /vertex\s+([-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?)\s+([-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?)\s+([-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?)/g;
    let vertexMatch;
    let vertexCount = 0;

    while ((vertexMatch = vertexPattern.exec(match[0])) !== null && vertexCount < 3) {
      vertices.push(parseFloat(vertexMatch[1]));
      vertices.push(parseFloat(vertexMatch[3]));
      vertices.push(parseFloat(vertexMatch[5]));

      normals.push(nx, ny, nz);
      vertexCount++;
    }
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(normals), 3));

  return geometry;
}

export default STLThumbnail;
